import React, { useState, useMemo } from "react";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox,
  CheckCircle2, Mail, Phone, CalendarDays, StickyNote, HandCoins, Clock, UserCheck,
} from "lucide-react";

/* Radha — Module 6: Payment Reminder Automation + AI Activity Capture (preview)
   Seeded to match 006_reminders.sql. RBI: window/cap/prohibited on every send. */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";

const SIGNAL_ICON = { email: Mail, call: Phone, meeting: CalendarDays, note: StickyNote, payment_promise: HandCoins };
const SENTIMENT = { positive: C.success, neutral: C.muted, negative: C.danger };

const SIGNALS = [
  { type: "payment_promise", source: "manual", cust: "Wipro Ltd", contact: "a***@wipro.com", summary: "Promise to pay recorded for 2026-06-18.", sentiment: "positive", ago: "2d" },
  { type: "call", source: "exotel", cust: "L&T Finance Holdings", contact: "f***@ltfinance.com", summary: "Finance contact confirmed payment will be processed by month-end.", sentiment: "positive", ago: "3d" },
  { type: "email", source: "gmail", cust: "Wipro Ltd", contact: "a***@wipro.com", summary: "Customer acknowledged the invoice and requested a 2-week extension.", sentiment: "neutral", ago: "5d" },
  { type: "meeting", source: "calendar", cust: "Infosys Ltd", contact: "v***@infosys.com", summary: "QBR meeting; expansion discussed, no payment concerns raised.", sentiment: "positive", ago: "8d" },
  { type: "email", source: "gmail", cust: "HDFC Bank Ltd", contact: "p***@hdfcbank.com", summary: "Dispute raised on platform charges; awaiting internal review.", sentiment: "negative", ago: "10d" },
];

const SCHEDULES = [
  { cust: "Reliance Industries Ltd", ref: "INV-2026-0301", channel: "email", cadence: 7, next: "2026-06-10", active: true },
  { cust: "Infosys Ltd", ref: "INV-2026-0303", channel: "whatsapp", cadence: 5, next: "2026-06-09", active: true },
  { cust: "Wipro Ltd", ref: "INV-2026-0305", channel: "whatsapp", cadence: 3, next: "2026-06-08", active: true },
  { cust: "L&T Finance Holdings", ref: "INV-2026-0306", channel: "email", cadence: 5, next: "2026-06-09", active: true },
  { cust: "Mahindra & Mahindra", ref: "INV-2026-0304", channel: "sms", cadence: 7, next: "2026-06-12", active: true },
  { cust: "Tata Consultancy Services", ref: "INV-2026-0302", channel: "email", cadence: 10, next: "2026-06-20", active: true },
];

const CONTACTS = [
  { cust: "Reliance Industries Ltd", name: "A. Sharma", role: "AP Lead", idle: 6, contactable: true },
  { cust: "Wipro Ltd", name: "R. Menon", role: "Finance Manager", idle: 2, contactable: true },
  { cust: "Mahindra & Mahindra", name: "S. Kulkarni", role: "Plant Accounts", idle: 40, contactable: true },
  { cust: "Tata Consultancy Services", name: "Unknown Contact", role: "—", idle: null, contactable: false },
];

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send, active: true },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>{children}</span>;
}
function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 140 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}

export default function Reminders() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(null);

  const idle = useMemo(() => CONTACTS.filter(c => c.idle === null || c.idle >= 30), []);
  const kpis = useMemo(() => ({
    active: SCHEDULES.filter(s => s.active).length,
    signals: SIGNALS.length,
    idle: idle.length,
    promises: SIGNALS.filter(s => s.type === "payment_promise").length,
  }), [idle]);

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .rrow:hover{background:${C.bg}!important}.nav:hover{background:#ffffff14}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M6 · PAYMENT REMINDERS + AI ACTIVITY CAPTURE</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Reminders & Activity Signals</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Active Schedules" value={kpis.active} sub="auto-reminding" accent={C.primary} />
          <KPICard label="Signals Captured" value={kpis.signals} sub="email · call · meeting" />
          <KPICard label="Idle Accounts" value={kpis.idle} sub="≥30 days silent" accent={C.warning} />
          <KPICard label="Promises to Pay" value={kpis.promises} sub="logged this week" accent={C.success} />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 540px", minWidth: 320 }}>
            {/* EF-CORE idle proposal */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · IDLE-DEAL RADAR</span>
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, color: C.ai }}>confidence 0.73</span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "#2A2A2E" }}>
                No activity captured from <b>Mahindra & Mahindra</b> in <b>40 days</b> and the open invoice is approaching
                its due date — the account looks idle. Recommended: a polite Hindi check-in via SMS and a contact refresh.
              </p>
              <button onClick={() => setDrawer({ cust: "Mahindra & Mahindra", channel: "sms", lang: "hi" })} style={{ marginTop: 12, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Review & send reminder</button>
            </div>

            {/* Activity signal feed */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "14px 16px 6px", marginBottom: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 10 }}>Captured activity feed</div>
              {view === "loading" ? <Skeleton /> : view === "error" ? <ErrorState /> : view === "empty" ? <EmptyState /> :
                SIGNALS.map((sig, i) => {
                  const Ic = SIGNAL_ICON[sig.type];
                  return (
                    <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bg, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <Ic size={15} color={SENTIMENT[sig.sentiment]} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{sig.cust}</span>
                          <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{sig.ago} ago</span>
                        </div>
                        <div style={{ fontSize: 12.5, color: "#2A2A2E", marginTop: 2 }}>{sig.summary}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 5, alignItems: "center" }}>
                          <Badge color={SENTIMENT[sig.sentiment]}>{sig.sentiment}</Badge>
                          <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{sig.type.replace("_", " ")} · {sig.source} · {sig.contact}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Schedule table */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px", borderBottom: `0.5px solid ${C.border}`, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Reminder schedules</div>
              {view === "data" && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Customer", "Ref", "Channel", "Cadence", "Next run"].map((h, i) => (
                    <th key={h} style={{ textAlign: "left", fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, fontWeight: 500, padding: "10px 14px", borderBottom: `0.5px solid ${C.border}` }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {SCHEDULES.map((s, i) => (
                      <tr key={i} className="rrow" onClick={() => setDrawer({ cust: s.cust, channel: s.channel, lang: "en" })} style={{ cursor: "pointer", borderBottom: `0.5px solid ${C.border}` }}>
                        <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500 }}>{s.cust}</td>
                        <td style={{ padding: "10px 14px", fontFamily: mono, fontSize: 11, color: C.muted }}>{s.ref}</td>
                        <td style={{ padding: "10px 14px" }}><Badge color={C.primary}>{s.channel}</Badge></td>
                        <td style={{ padding: "10px 14px", fontFamily: mono, fontSize: 12 }}>every {s.cadence}d</td>
                        <td style={{ padding: "10px 14px", fontFamily: mono, fontSize: 12, color: C.muted }}>{s.next.slice(5)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Idle radar + contacts */}
          <div style={{ flex: "0 0 250px", minWidth: 210 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <Clock size={13} color={C.warning} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Contact enrichment</span>
              </div>
              {CONTACTS.map((c, i) => (
                <div key={i} style={{ marginTop: 12, paddingTop: i ? 12 : 0, borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>{c.name}</span>
                    {c.contactable
                      ? <UserCheck size={13} color={C.success} />
                      : <Badge color={C.muted}>no channel</Badge>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                    <span style={{ fontSize: 11, color: C.muted }}>{c.cust.split(" ")[0]} · {c.role}</span>
                    {c.idle === null
                      ? <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>no activity</span>
                      : <span style={{ fontFamily: mono, fontSize: 10, color: c.idle >= 30 ? C.warning : C.muted }}>{c.idle}d idle</span>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Compliance</div>
              {[["RBI contact window", "08:00–19:00 IST"], ["Max 3 reminders/day", "shared with M1"], ["GDPR activity logs", "contacts masked"]].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12 }}>
                  <ShieldCheck size={15} color={C.success} style={{ marginTop: 1, flexShrink: 0 }} />
                  <div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{r[0]}</div><div style={{ fontSize: 11, color: C.muted }}>{r[1]}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <SendDrawer target={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function Skeleton() { return <div style={{ padding: "4px 0 14px" }}>{[...Array(4)].map((_, i) => <div key={i} style={{ height: 18, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>; }
function ErrorState() { return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load signals</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>; }
function EmptyState() { return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No activity captured yet</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Connect Gmail / Exotel / Calendar to ingest signals.</div></div>; }

function SendDrawer({ target, onClose }) {
  const [channel, setChannel] = useState(target.channel || "email");
  const [body, setBody] = useState(target.lang === "hi"
    ? "Namaste, yeh ek vinamra anurodh hai. Kripya bakaya rashi ke baare mein humse sampark karein."
    : "Gentle reminder: we'd value an update on your account. Please reach out at your convenience to discuss the outstanding balance.");
  const [done, setDone] = useState(false);
  const PROHIBITED = ["threaten", "sue", "police", "court", "jail", "seize", "attach", "ruin"];
  const found = PROHIBITED.filter(w => new RegExp(`\\b${w}\\b`, "i").test(body));
  const istHour = new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", hour12: false });
  const inWindow = +istHour >= 8 && +istHour < 19;
  const blocked = found.length > 0 || !inWindow;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 390, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Send reminder</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <h3 style={{ margin: "8px 0 8px", fontSize: 17 }}>{target.cust}</h3>

        <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Channel</div>
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {["sms", "email", "whatsapp"].map(c => (
            <button key={c} onClick={() => setChannel(c)} style={{ flex: 1, fontSize: 12, padding: "8px 0", borderRadius: 8, cursor: "pointer", border: `0.5px solid ${channel === c ? C.primary : C.border}`, background: channel === c ? `${C.primary}10` : C.surface, color: channel === c ? C.primary : C.text, textTransform: "capitalize" }}>{c}</button>
          ))}
        </div>

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Message</div>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} style={{ width: "100%", marginTop: 8, padding: 10, fontSize: 13, lineHeight: 1.5, border: `0.5px solid ${found.length ? C.danger : C.border}`, borderRadius: 8, resize: "vertical", fontFamily: sans, color: C.text, boxSizing: "border-box" }} />

        <div style={{ marginTop: 12, background: blocked ? "#FBE9E7" : "#E9F7F1", border: `0.5px solid ${(blocked ? C.danger : C.success)}33`, borderRadius: 8, padding: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {blocked ? <AlertTriangle size={14} color={C.danger} /> : <ShieldCheck size={14} color={C.success} />}
            <span style={{ fontSize: 12, fontWeight: 500, color: blocked ? C.danger : C.success }}>{blocked ? "Blocked by RBI guard" : "Passes RBI checks"}</span>
          </div>
          <ul style={{ margin: "8px 0 0 18px", padding: 0, fontSize: 11.5, color: C.muted }}>
            <li>Window 08:00–19:00 IST — {inWindow ? "within window" : `outside (now ${istHour}:00 IST)`}</li>
            <li>Prohibited language — {found.length ? `found: ${found.join(", ")}` : "none"}</li>
            <li>Daily cap (shared w/ M1) — under 3 contacts today</li>
          </ul>
        </div>

        {done ? (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, background: "#E9F7F1", border: `0.5px solid ${C.success}33`, borderRadius: 9, padding: 12, fontSize: 13, color: C.success }}>
            <CheckCircle2 size={15} /> Reminder sent via {channel}. Logged to the audit trail.
          </div>
        ) : (
          <button disabled={blocked} onClick={() => setDone(true)} style={{ width: "100%", marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: blocked ? C.border : C.primary, color: blocked ? C.muted : "#fff", border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500, cursor: blocked ? "not-allowed" : "pointer" }}>
            <Send size={14} /> {blocked ? "Cannot send" : `Send via ${channel}`}
          </button>
        )}
      </div>
    </div>
  );
}
